/**
 * shared/apiserpent.ts — the PURE half of the apiserpent shopping adapter
 * (card GnfrAFZ6lBpM, Phase 2b; spec g4DBeUqactRn).
 *
 * Response shape is normalised from a REAL payload captured in the Phase 1
 * probe (`scripts/fixtures/apiserpent/amazon_search_au_2026-10-07.json`):
 * a top-level `results[]` of product cards carrying price / list_price /
 * currency / rating / ratings_total / badge / delivery, plus `search_term`
 * and `results_count` at the root. The fetch half lives in
 * `worker/src/apiserpent.ts`.
 *
 * The engine is an ARGUMENT, not a branch: `amazon` is the endpoint that is
 * live today, `shopping` (Google Shopping) answers 404 until apiserpent
 * launches it — the day it does, the caller passes engine: 'shopping' and
 * nothing in the agent moves. Nothing here touches the network, so a probe
 * can import it and assert against the committed fixture without a key.
 */

/** 'serppost' is the organic+Reader adapter (worker/src/serppost.ts): the
 *  country-localised fallback when the shopping engines are unkeyed/dry. */
export type ShopEngine = 'amazon' | 'shopping' | 'serppost';

export const SHOP_ENGINES: readonly ShopEngine[] = ['amazon', 'shopping', 'serppost'];

export const SHOP_ENDPOINTS: Record<ShopEngine, string> = {
  amazon: 'https://apiserpent.com/api/amazon/search',
  shopping: 'https://apiserpent.com/api/shopping',
  serppost: 'https://serppost.com/api/v1/search',
};

/** AU marketplace — the one the Phase 1 probe captured. */
export const AMAZON_MARKETPLACE = 'amazon.com.au';

/** One product, already flattened across whatever field names the engine used. */
export interface ProductRecord {
  title: string;
  /** '' when the engine returned a card with no link of its own. */
  url: string;
  price: number | null;
  /** ISO-4217 exactly as the engine sent it ('USD', 'AUD', ...). */
  currency: string | null;
  listPrice: number | null;
  /** 0-5 stars. */
  rating: number | null;
  ratingsCount: number | null;
  image: string | null;
  asin: string | null;
  /** 'Overall Pick', 'Best Seller', ... — a label, never a bare flag. */
  badge: string | null;
  /** Delivery / availability line, verbatim. */
  availability: string | null;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' ? (v.trim() || null) : null;
}

/**
 * One row -> one record. Missing fields stay null rather than dropping the
 * row: a card with a title and no price is still something to compare, and
 * a normaliser that hides half the page because one field is absent reads
 * as "the search found nothing".
 *
 * `rating` arrives as a bare number on the amazon engine and could arrive
 * as `{rate, count}` on another — the second shape is read if present, and
 * both fall through to null. `price` likewise accepts `extracted_price`,
 * the common name on shopping engines.
 */
function normaliseRow(raw: unknown): ProductRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const title = str(r.title) ?? str(r.name);
  if (!title) return null;
  const ratingObj = (r.rating && typeof r.rating === 'object' ? r.rating : null) as
    Record<string, unknown> | null;
  const rating = num(ratingObj ? ratingObj.rate : r.rating);
  const ratingsCount = num(ratingObj ? ratingObj.count : r.ratings_total)
    ?? num(r.reviews_count)
    // SerpAPI's shopping rows count reviews as `reviews`.
    ?? num(r.reviews);
  return {
    title,
    // Three engines, three names for the same thing: apiserpent's `url`,
    // Google's `link`, and SerpAPI's google_shopping `product_link` (the
    // only one that engine sends — without it every Google Shopping row
    // normalises to a card with nowhere to open, and the enrichment has
    // prices but no links).
    url: str(r.url) ?? str(r.link) ?? str(r.product_link) ?? '',
    price: num(r.price) ?? num(r.extracted_price),
    currency: str(r.currency),
    listPrice: num(r.list_price) ?? num(r.old_price),
    rating,
    ratingsCount,
    image: str(r.image) ?? str(r.thumbnail),
    asin: str(r.asin),
    badge: str(r.badge),
    availability: str(r.delivery) ?? str(r.availability),
  };
}

/**
 * Body -> records, deduped on url / asin / lowercased title (one product
 * can appear twice on a page as a sponsored row and an organic one).
 *
 * No row cap here: the PAGE is what bounds the result (16-48 cards), and a
 * normaliser that truncated would silently discard rows a caller asked for —
 * callers slice what they show (`productRationale` takes 3). An
 * unparseable body yields an EMPTY list, never a throw: the caller reports
 * `no_results`, a state the route knows how to show.
 */
export function normaliseProducts(body: unknown, _engine: ShopEngine): ProductRecord[] {
  const root = (body ?? {}) as Record<string, unknown>;
  const list = Array.isArray(root.results)
    ? root.results
    : Array.isArray(root.shopping_results)
      ? root.shopping_results
      : [];
  const out: ProductRecord[] = [];
  const seen = new Set<string>();
  for (const raw of list as unknown[]) {
    const p = normaliseRow(raw);
    if (!p) continue;
    const key = p.url || p.asin || p.title.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/** 'US$27.85' / 'A$12.00' / '£9.99' / 'EUR 9.99'. Two decimals unless the
 *  engine sent a whole number. */
export function formatPrice(price: number | null, currency: string | null): string | null {
  if (price === null) return null;
  const shown = Number.isInteger(price) ? String(price) : price.toFixed(2);
  const code = (currency ?? '').toUpperCase();
  const symbol = code === 'USD' ? 'US$'
    : code === 'AUD' ? 'A$'
    : code === 'GBP' ? '£'
    : code === 'EUR' ? '€'
    : code === 'CAD' ? 'C$'
    : code === 'NZD' ? 'NZ$'
    : '';
  return symbol ? `${symbol}${shown}` : code ? `${code} ${shown}` : shown;
}

/** 'US$27.85 (was US$34.99)' — the list price only when it is actually lower. */
function pricePhrase(p: ProductRecord): string | null {
  const now = formatPrice(p.price, p.currency);
  if (now === null) return null;
  const was = formatPrice(p.listPrice, p.currency);
  if (was !== null && p.listPrice !== null && p.price !== null && p.listPrice > p.price) {
    return `${now} (was ${was})`;
  }
  return now;
}

/** '4.6/5 from 46,608' — rating without a count is just '4.6/5'. */
function ratingPhrase(p: ProductRecord): string | null {
  if (p.rating === null) return null;
  const base = `${p.rating}/5`;
  if (p.ratingsCount === null) return base;
  return `${base} from ${p.ratingsCount.toLocaleString('en-US')}`;
}

/** 'Title — US$27.85 (was US$34.99), 4.6/5 from 46,608, Overall Pick' */
export function describeProduct(p: ProductRecord): string {
  const bits = [pricePhrase(p), ratingPhrase(p), p.badge].filter(Boolean) as string[];
  return bits.length ? `${p.title} — ${bits.join(', ')}` : p.title;
}

/**
 * The rationale line the recommendation card shows when nothing saved fits.
 * Card dffMAMTEu2gb (design doc §5): ONE sentence about THIS pick — what it
 * is and what it costs — grounded in the recipient's occasion/interest when
 * the caller knows them. No "Top match", no rating ("2.9/5 from 1"), no badge,
 * no "Also:" tail listing other products: the stack under the card already
 * shows the alternatives, so the sentence only has to explain the one in
 * front of the user. Everything it claims (title, price) comes from the
 * engine's own row, so it can be checked against the fixture it was built
 * from. Empty string means "keep the existing rationale" — a search that
 * found nothing must not overwrite 'Nothing saved yet fits' with a blank.
 */
export interface RationaleContext {
  /** The occasion label ('B's birthday') — it already names the recipient,
   *  which is what makes the sentence about a person instead of a search. */
  occasion?: string | null;
  /** The recipient interest topic the query was built from ('coastal decor'). */
  subject?: string | null;
}

export function productRationale(
  products: ProductRecord[],
  max = 3,
  ctx: RationaleContext = {},
): string {
  const top = products.slice(0, Math.max(1, max));
  if (top.length === 0) return '';
  const [first] = top;
  const price = pricePhrase(first);
  const what = price ? `${first.title} — ${price}` : first.title;
  // The grounding is bracketed, so the prefix sentence reads on its own when
  // the worker has no occasion (anytime) or no saved interest for the pair.
  const grounding = [ctx.occasion, ctx.subject].filter(Boolean).join(', ');
  const line = grounding
    ? `Nothing saved yet fits. Closest match for ${grounding}: ${what}.`
    : `Nothing saved yet fits. Closest match: ${what}.`;
  return line.slice(0, 1000);
}

/**
 * Card ggTQe2KbB0A3 MEDIUM-1 — ONE sentence about ONE displayed pick.
 * Every stack card used to render the recommendation's single rationale,
 * which productRationale() builds from the FIRST product only, so flicking
 * to product two showed product one's reason. This names the product the
 * card is actually showing: the first pick keeps the exact rec-level
 * sentence (so card one and the fallback card never disagree), later picks
 * get their own line naming their own title and price.
 */
export function pickRationale(
  p: ProductRecord,
  ctx: RationaleContext = {},
  isFirst = false,
): string {
  if (isFirst) return productRationale([p], 1, ctx);
  const price = pricePhrase(p);
  const what = price ? `${p.title} — ${price}` : p.title;
  const grounding = [ctx.occasion, ctx.subject].filter(Boolean).join(', ');
  const line = grounding
    ? `Nothing saved yet fits. Also worth a look for ${grounding}: ${what}.`
    : `Nothing saved yet fits. Also worth a look: ${what}.`;
  return line.slice(0, 1000);
}

/** Prefix every enriched rationale carries — the read side of the sentence
 *  above. The caller checks this BEFORE searching so a second POST for the
 *  same occasion cannot spend a second call on a pick it already enriched:
 *  one search per recommendation, not one per request. BOTH generations are
 *  accepted: a row enriched before dffMAMTEu2gb says "Top match:", and a
 *  repeat POST must not re-buy that search just because the copy changed. */
export const PRODUCT_RATIONALE_PREFIX = 'Nothing saved yet fits.';
const LEGACY_PRODUCT_RATIONALE_PREFIX = 'Nothing saved yet fits. Top match:';

export function isProductRationale(rationale: string): boolean {
  return rationale.startsWith(PRODUCT_RATIONALE_PREFIX)
    || rationale.startsWith(LEGACY_PRODUCT_RATIONALE_PREFIX);
}
